import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import useAxios from "@hooks/use-axios";
import usePublisherKyc from "./use-publisher-kyc";
import { defaultPublisherFilters, toPublisherFilterParams } from "../misc/schema";
import { rowsToCsvBlob, triggerDownload } from "../misc/csv";
import { PUBLISHERS_CSV_COLUMNS, PUBLISHERS_DEFAULT_LIMIT, PUBLISHERS_ENDPOINT, PUBLISHERS_EXPORT_ENDPOINT, publisherDetailsPath, publisherDetailsState } from "../misc/constants";

// The search box fires on every keystroke — wait for typing to settle before
// re-fetching so each character doesn't send its own request.
const FILTER_DEBOUNCE_MS = 400;

const usePublishers = () => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true });
    // The export endpoint isn't live yet and is expected to fail over to a client-side
    // CSV (see exportCsv below) — route it through a silent instance so that expected
    // failure doesn't also surface the shared error toast alongside the successful download.
    const { axiosInstance: exportAxiosInstance } = useAxios({ base: "ops", showAlert: false });
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PUBLISHERS_DEFAULT_LIMIT });
    const [filters, setFilters] = useState(defaultPublisherFilters);
    const navigate = useNavigate();

    const fetchPublishers = async (page = 1, limit = PUBLISHERS_DEFAULT_LIMIT, currentFilters = filters) => {
        const params = await toPublisherFilterParams(currentFilters);
        try {
            const res = await axiosInstance.get(PUBLISHERS_ENDPOINT, { params: { page, limit, ...params } });
            const result = res.data || res;
            const list = result.data || result.items || result.results || [];
            setRows(list);
            setPagination({
                total: result.pagination?.total ?? result.total ?? list.length,
                page : result.pagination?.page ?? result.page ?? page,
                limit: result.pagination?.limit ?? result.limit ?? limit
            });
        } catch {
            setRows([]);
            setPagination({ total: 0, page, limit });
        }
    };

    const kyc = usePublisherKyc({ onChanged: () => fetchPublishers(pagination.page, pagination.limit, filters) });

    useEffect(() => {
        const timer = setTimeout(() => fetchPublishers(1, pagination.limit, filters), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchPublishers(page, limit, filters);
    const handlePerRowsChange = (limit) => fetchPublishers(1, limit, filters);

    const handleFilterChange = (nextFilters) => {
        const merged = { ...filters, ...nextFilters };
        setFilters(merged);
    };

    const openPublisher = (publisher) => navigate(publisherDetailsPath(publisher._id), { state: publisherDetailsState(publisher) });

    const exportCsv = async () => {
        try {
            const params = await toPublisherFilterParams(filters);
            const blob = await exportAxiosInstance.get(PUBLISHERS_EXPORT_ENDPOINT, { params, responseType: "blob" });
            triggerDownload(blob, "publishers.csv");
        } catch {
            // Endpoint isn't live yet — export whatever's currently loaded on screen.
            triggerDownload(rowsToCsvBlob(rows, PUBLISHERS_CSV_COLUMNS), "publishers.csv");
        }
    };

    return {
        rows,
        pagination,
        loading,
        filters,
        handleFilterChange,
        handlePageChange,
        handlePerRowsChange,
        openPublisher,
        kyc,
        exportCsv
    };
};

export default usePublishers;
